import { isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface MenuChangeEvent {
  key: string;
  routeEvent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private readonly STORAGE_KEY = 'layout_anchored';
  private readonly MENU_STATE_KEY = 'layout_menu_state';
  private readonly MOBILE_BREAKPOINT = 992;
  private platformId = inject(PLATFORM_ID);

  /** Drawer layout mode — hardcoded, no other modes needed */
  readonly layoutMode = signal<string>('drawer');

  /** Sidebar expanded on hover (non-anchored state) */
  readonly sidebarActive = signal(false);

  /** Sidebar pinned open — persisted to localStorage */
  readonly anchored = signal(false);

  /** Mobile overlay menu open */
  readonly mobileMenuActive = signal(false);

  /** Profile sidebar visible */
  readonly profileSidebarVisible = signal(false);

  /** Menu group expand/collapse state — persisted to localStorage */
  private readonly menuState = signal<Record<string, boolean>>(this.restoreMenuState());

  /** Menu accordion: notifies sibling menu items to collapse */
  private readonly menuSource = new Subject<MenuChangeEvent>();
  readonly menuSource$ = this.menuSource.asObservable();

  onMenuStateChange(event: MenuChangeEvent): void {
    this.menuSource.next(event);
  }

  /** CSS class object for layout-container, driven by layout state signals */
  readonly containerClass = computed(() => ({
    'layout-drawer': true,
    'layout-colorscheme-menu': true,
    'layout-sidebar-active': this.sidebarActive() || this.anchored(),
    'layout-sidebar-anchored': this.anchored(),
    'layout-mobile-active': this.mobileMenuActive(),
  }));

  constructor() {
    this.restoreAnchored();
  }

  /** Check if a menu group is expanded */
  isMenuActive(key: string): boolean {
    return this.menuState()[key] ?? false;
  }

  /** Set a menu group expand/collapse state and persist */
  setMenuActive(key: string, active: boolean): void {
    if ((this.menuState()[key] ?? false) === active) return;
    this.menuState.update((state) => ({ ...state, [key]: active }));
    this.persistMenuState();
  }

  onSidebarMouseEnter(): void {
    if (!this.anchored()) {
      this.sidebarActive.set(true);
    }
  }

  onSidebarMouseLeave(): void {
    if (!this.anchored()) {
      this.sidebarActive.set(false);
    }
  }

  toggleAnchor(): void {
    this.anchored.update((v) => !v);
    this.persistAnchored();
  }

  toggleMobileMenu(): void {
    this.mobileMenuActive.update((v) => !v);
    if (this.mobileMenuActive()) {
      this.blockBodyScroll();
    } else {
      this.unblockBodyScroll();
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuActive.set(false);
    this.unblockBodyScroll();
  }

  /**
   * Menu button click handler — context-dependent:
   * - Desktop: toggles sidebar anchor (pin/unpin)
   * - Mobile: toggles mobile overlay menu
   */
  onMenuButtonClick(): void {
    if (this.isMobile()) {
      this.toggleMobileMenu();
    } else {
      this.toggleAnchor();
    }
  }

  private isMobile(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return window.innerWidth < this.MOBILE_BREAKPOINT;
  }

  private blockBodyScroll(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.add('blocked-scroll');
    }
  }

  private unblockBodyScroll(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('blocked-scroll');
    }
  }

  private persistAnchored(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.anchored()));
  }

  private restoreAnchored(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored === 'true') {
      this.anchored.set(true);
    }
  }

  private persistMenuState(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.MENU_STATE_KEY, JSON.stringify(this.menuState()));
  }

  private restoreMenuState(): Record<string, boolean> {
    if (!isPlatformBrowser(this.platformId)) return {};
    const stored = localStorage.getItem(this.MENU_STATE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as Record<string, boolean>;
      } catch {
        return {};
      }
    }
    return {};
  }
}
